import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  PaginationItem,
  Chip,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import WestIcon from "@mui/icons-material/West";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LoginHistory = () => {
  const navigate = useNavigate();
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [hoveredField, setHoveredField] = useState(null);
  
  // Filter state
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchLoginHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, sortField, sortOrder]);

  const fetchLoginHistory = async (filterFromDate = undefined, filterToDate = undefined, customPagination = null) => {
    setLoading(true);
    try {
      // Use provided dates if explicitly passed (including null), otherwise use state
      const dateFrom = filterFromDate !== undefined ? filterFromDate : fromDate;
      const dateTo = filterToDate !== undefined ? filterToDate : toDate;
      const paginationToUse = customPagination || pagination;
      
      const response = await api.post("/loginHistory", {
        page: paginationToUse.page,
        limit: paginationToUse.limit,
        sortField: sortField,
        sortOrder: sortOrder,
        fromDate: dateFrom ? dayjs(dateFrom).format("YYYY-MM-DD") : null,
        toDate: dateTo ? dayjs(dateTo).format("YYYY-MM-DD") : null,
      });
      
      if (response && response.success) {
        setLoginHistory(response.data);
        setPagination({
          ...paginationToUse,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        });
      } else {
        toast.error(response?.message || "Failed to fetch login history", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "toast-error",
        });
      }
    } catch (error) {
      console.error("Error fetching login history:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch login history", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: "toast-error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination({ ...pagination, page: value });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
    setPagination({ ...pagination, page: 1 }); // Reset to first page on sort
  };

  const handleFilterApply = () => {
    const updatedPagination = { ...pagination, page: 1 };
    setPagination(updatedPagination);
    // Trigger fetch with current filter values from state and reset pagination
    fetchLoginHistory(fromDate, toDate, updatedPagination);
  };

  const handleFilterClear = () => {
    setFromDate(null);
    setToDate(null);
    const updatedPagination = { ...pagination, page: 1 };
    setPagination(updatedPagination);
    // Trigger fetch with cleared filters (explicitly pass null) and reset pagination
    fetchLoginHistory(null, null, updatedPagination);
  };

  const formatTechnicalSpecs = (specs) => {
    try {
      if (!specs || specs === "N/A") return "N/A";
      
      let userAgent = specs;
      let platform = null;
      
      // Try to parse if it's JSON (from backend)
      try {
        const parsed = JSON.parse(specs);
        userAgent = parsed.userAgent || specs;
        platform = parsed.platform || null;
      } catch {
        userAgent = specs;
      }

      // Parse user agent string to user-friendly format
      const parseUserAgent = (ua, platformInfo) => {
        if (!ua) return "N/A";

        // Browser detection
        let browser = "Unknown Browser";
        let browserVersion = "";
        
        if (ua.includes("Edg/")) {
          browser = "Microsoft Edge";
          const match = ua.match(/Edg\/(\d+\.\d+\.\d+\.\d+)/);
          if (match) browserVersion = match[1];
        } else if (ua.includes("Chrome/") && !ua.includes("Edg/")) {
          browser = "Google Chrome";
          const match = ua.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
          if (match) browserVersion = match[1];
        } else if (ua.includes("Firefox/")) {
          browser = "Mozilla Firefox";
          const match = ua.match(/Firefox\/(\d+\.\d+)/);
          if (match) browserVersion = match[1];
        } else if (ua.includes("Safari/") && !ua.includes("Chrome/")) {
          browser = "Safari";
          const match = ua.match(/Version\/(\d+\.\d+)/);
          if (match) browserVersion = match[1];
        } else if (ua.includes("Opera/") || ua.includes("OPR/")) {
          browser = "Opera";
          const match = ua.match(/(?:Opera|OPR)\/(\d+\.\d+)/);
          if (match) browserVersion = match[1];
        }

            // OS detection - use platform header if available (more accurate)
            let os = "Unknown OS";
            
            // Check platform header first (more reliable for Windows 11)
            // Only use platform header if it's not "Unknown" or empty
            if (platformInfo && platformInfo !== "Unknown" && platformInfo.trim() !== "") {
              // Platform header format: "Windows" or "\"Windows\"" (with quotes)
              const cleanPlatform = platformInfo.replace(/"/g, '').trim();
              if (cleanPlatform.toLowerCase().includes("windows")) {
                // If platform says Windows and UA has NT 10.0, it could be Windows 11
                // But we can't be 100% sure, so we'll use the platform info
                if (ua.includes("Windows NT 11.0")) {
                  os = "Windows 11";
                } else if (ua.includes("Windows NT 10.0")) {
                  // Windows 11 often reports as 10.0, but platform header might help
                  // For now, show as Windows 10/11 since we can't reliably distinguish
                  os = "Windows 10/11";
                } else {
                  os = cleanPlatform;
                }
              } else {
                os = cleanPlatform;
              }
            } else {
              // Fallback to user agent parsing when platform header is missing or "Unknown"
              if (ua.includes("Windows NT 11.0")) {
                os = "Windows 11";
              } else if (ua.includes("Windows NT 10.0")) {
                // Windows 11 often reports as 10.0 for compatibility
                // Show as Windows 10/11 since we can't reliably distinguish
                os = "Windows 10/11";
              } else if (ua.includes("Windows NT 6.3")) {
                os = "Windows 8.1";
              } else if (ua.includes("Windows NT 6.2")) {
                os = "Windows 8";
              } else if (ua.includes("Windows NT 6.1")) {
                os = "Windows 7";
              } else if (ua.includes("Mac OS X")) {
                const match = ua.match(/Mac OS X (\d+[._]\d+)/);
                if (match) {
                  os = `macOS ${match[1].replace("_", ".")}`;
                } else {
                  os = "macOS";
                }
              } else if (ua.includes("Linux")) {
                os = "Linux";
              } else if (ua.includes("Android")) {
                const match = ua.match(/Android (\d+\.\d+)/);
                if (match) {
                  os = `Android ${match[1]}`;
                } else {
                  os = "Android";
                }
              } else if (ua.includes("iPhone") || ua.includes("iPad")) {
                os = ua.includes("iPad") ? "iPadOS" : "iOS";
              }
            }

        // Build user-friendly string
        const parts = [];
        if (browser !== "Unknown Browser") {
          parts.push(`${browser}${browserVersion ? ` ${browserVersion}` : ""}`);
        } else {
          // Try to extract browser name from user agent if detection failed
          if (ua.includes("Chrome")) {
            parts.push("Chrome");
          } else if (ua.includes("Firefox")) {
            parts.push("Firefox");
          } else if (ua.includes("Safari")) {
            parts.push("Safari");
          } else if (ua.includes("Edge")) {
            parts.push("Edge");
          } else if (ua.includes("Opera")) {
            parts.push("Opera");
          }
        }
        
        if (os !== "Unknown OS") {
          parts.push(`on ${os}`);
        } else {
          // Try to extract OS from user agent if detection failed
          if (ua.includes("Windows")) {
            parts.push("on Windows");
          } else if (ua.includes("Mac")) {
            parts.push("on macOS");
          } else if (ua.includes("Linux")) {
            parts.push("on Linux");
          } else if (ua.includes("Android")) {
            parts.push("on Android");
          } else if (ua.includes("iPhone") || ua.includes("iPad")) {
            parts.push(ua.includes("iPad") ? "on iPadOS" : "on iOS");
          }
        }

        // If we still have no parts, return empty string (don't show anything)
        return parts.length > 0 ? parts.join(" ") : "";
      };

      return parseUserAgent(userAgent, platform);
    } catch {
      return specs || "N/A";
    }
  };

  const getSortIcon = (field) => {
    // Show icon if field is sorted or if hovering over it
    if (sortField === field) {
      return sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
    }
    // Show up arrow on hover if not sorted
    if (hoveredField === field) {
      return <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    }
    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <IconButton
          onClick={() => navigate("/dashboard")}
          sx={{
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <WestIcon sx={{ color: "#1D2939" }} />
        </IconButton>
        <Typography 
          variant="h6"
          onClick={() => navigate("/dashboard")}
          sx={{ 
            fontWeight: 600, 
            color: "#1D2939", 
            flex: 1, 
            fontSize: "18px",
            cursor: "pointer"
          }}
        >
          My Login History
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ textTransform: "none" }}
        >
          Filter
        </Button>
      </Box>

      {/* Date Filter Section */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              onClick={handleFilterApply}
              sx={{ textTransform: "none" }}
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              onClick={handleFilterClear}
              sx={{ textTransform: "none" }}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ borderCollapse: "separate" }}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("sl_no")}
                      onMouseEnter={() => setHoveredField("sl_no")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Sl.No
                        {getSortIcon("sl_no")}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("ip_address")}
                      onMouseEnter={() => setHoveredField("ip_address")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        IP Address
                        {getSortIcon("ip_address")}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("technical_specs")}
                      onMouseEnter={() => setHoveredField("technical_specs")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Technical Specs
                        {getSortIcon("technical_specs")}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("login_time")}
                      onMouseEnter={() => setHoveredField("login_time")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Login Time
                        {getSortIcon("login_time")}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("logout_time")}
                      onMouseEnter={() => setHoveredField("logout_time")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Logout Time
                        {getSortIcon("logout_time")}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "14px",
                        color: "#1D2939", 
                        cursor: "pointer",
                        py: 1.5,
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: "rgba(216, 154, 29, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(216, 154, 29, 0.4)"
                        }
                      }}
                      onClick={() => handleSort("session_time")}
                      onMouseEnter={() => setHoveredField("session_time")}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Session Time
                        {getSortIcon("session_time")}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No login history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    loginHistory.map((history, index) => (
                      <TableRow 
                        key={index} 
                        hover 
                        sx={{ 
                          backgroundColor: index % 2 === 0 ? "transparent" : "#f3e0c032",
                          "&:hover": {
                            backgroundColor: "#f3e0c032"
                          },
                          borderBottom: "1px solid #E5E7EB"
                        }}
                      >
                        <TableCell sx={{ py: 1.5, fontSize: "14px", color: "#1D2939" }}>{history.sl_no}</TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: "14px", color: "#1D2939" }}>{history.ip_address}</TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: "14px", color: "#1D2939" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                              color: "#1D2939"
                            }}
                            title={formatTechnicalSpecs(history.technical_specs)}
                          >
                            {formatTechnicalSpecs(history.technical_specs)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: "14px", color: "#1D2939" }}>{history.login_time}</TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: "14px" }}>
                          {history.logout_time === "Active" ? (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <span style={{ color: "#1D2939" }}>{history.logout_time}</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: "14px", color: "#1D2939" }}>{history.session_time}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              p: 2,
              borderTop: "1px solid #E5E7EB"
            }}>
              <Typography variant="body2" sx={{ color: "#667085", fontSize: "14px" }}>
                Showing {loginHistory.length > 0 ? ((pagination.page - 1) * pagination.limit + 1) : 0} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </Typography>
              {pagination.totalPages > 0 && (
                <Stack spacing={2} direction="row" justifyContent="center">
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        onClick={() => handlePageChange(null, item.page)}
                        disabled={item.page === "..." || (item.type === "previous" && pagination.page === 1) || (item.type === "next" && (loginHistory.length === 0 || pagination.page === pagination.totalPages))}
                        sx={{
                          mx: 0.5,
                          cursor: item.page === "..." ? "default" : "pointer",
                          backgroundColor: pagination.page === item.page ? "#1976d2" : "transparent",
                          color: pagination.page === item.page ? "#fff" : "inherit",
                        }}
                      />
                    )}
                  />
                </Stack>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoginHistory;
